import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import {
  CONNECTION_STATUS,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';
import { useQueryListHardwareDevices } from '@/hooks/query/desktop/chronicle/useQueryListHardwareDevices';
import { logger } from '@/utils/logger/logger';

/**
 * Restores linked device state from backend API on mount.
 *
 * After app restart in-memory Jotai state is lost. This hook uses
 * `useQueryListHardwareDevices` to check if the user has a linked device
 * and populates the atoms so DeviceCard is shown immediately.
 *
 * Skips the API call when a linked device is already known or BLE is active.
 */
export function useRestoreLinkedDevice({
  linkedDeviceId,
  connectionStatus,
  enabled = true,
}: {
  linkedDeviceId: string | null;
  connectionStatus: string;
  /** Gate the restore (e.g. behind auth + the Chronicle flag) so it doesn't
   * fetch `/hardware-device/` for accounts without the feature. Defaults to
   * `true` so existing call sites are unchanged. */
  enabled?: boolean;
}) {
  const setConnectionContext = useSetAtom(chronicleConnectionContextAtom);
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);

  const shouldRestore =
    enabled &&
    linkedDeviceId === null &&
    connectionStatus === CONNECTION_STATUS.DISCONNECTED;
  const { data: devices, error: devicesError } =
    useQueryListHardwareDevices(shouldRestore);

  useEffect(() => {
    if (devicesError) {
      logger.warn({
        msg: 'Failed to restore linked device from backend',
        error:
          devicesError instanceof Error ? devicesError.message : String(devicesError),
      });
    }
  }, [devicesError]);

  useEffect(() => {
    if (!devices) return;
    const device = devices[0]; // TODO: support multiple devices
    if (!device) return;

    setConnectionContext((prev) => ({
      ...prev,
      linkedDeviceId: device.id,
    }));
    setDeviceState((prev) => ({
      ...prev,
      deviceName: device.device_name ?? prev.deviceName,
      // `device` is the backend hardware_device record (lags — only PATCHed on OTA
      // completion); `prev.firmwareVersion` is the live BLE APP&FW value and wins.
      // Backend only fills in before BLE has reported (cold start), never overwrites.
      firmwareVersion: prev.firmwareVersion ?? device.firmware_version ?? null,
      serialNumber: device.serial_number ?? prev.serialNumber ?? null,
    }));
  }, [devices, setConnectionContext, setDeviceState]);
}
